import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../../lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Loader2, Save, CheckCircle2, X, Users,
  AlertCircle,
} from "lucide-react";

const errMsg = (e, f = "Failed") => {
  const d = e?.response?.data?.detail;
  if (!d) return f;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return JSON.stringify(d);
};

// Monday of the given date (or current) in local time.
function mondayOf(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const day = d.getDay(); // 0 sun .. 6 sat
  const diff = (day + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function TimeAllocationTab({ project }) {
  const [weekStart, setWeekStart] = useState(mondayOf());
  const [data, setData] = useState({ entries: [], phases: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // entry currently being allocated

  const load = () => {
    setLoading(true);
    api.get(`/projects/${project.id}/time-allocations?week_start=${weekStart}`)
      .then(setData)
      .catch(() => setData({ entries: [], phases: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [project.id, weekStart]);

  const totals = useMemo(() => {
    const t = { hours: 0, allocated: 0, unallocated: 0, cost: 0 };
    data.entries.forEach((e) => {
      const h = parseFloat(e.hours) || 0;
      const a = parseFloat(e.allocated_hours) || 0;
      t.hours += h;
      t.allocated += a;
      t.unallocated += Math.max(0, h - a);
      t.cost += h * (parseFloat(e.hourly_rate) || 0);
    });
    return t;
  }, [data]);

  const pctAllocated = totals.hours > 0 ? (totals.allocated / totals.hours * 100) : 0;

  const weekEnd = addDays(weekStart, 6);

  return (
    <div className="space-y-3">
      {/* Week nav + summary */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <button data-testid="prj-alloc-prev-week"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
          <input
            data-testid="prj-alloc-week-input"
            type="date" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))}
            className="rounded border border-slate-300 px-2 py-1 text-xs"
          />
          <button data-testid="prj-alloc-next-week"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="p-1.5 rounded hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
          <div className="text-[11px] text-slate-500 ml-2">Week of <b>{weekStart}</b> → {weekEnd}</div>
        </div>
        <button
          data-testid="prj-alloc-this-week"
          onClick={() => setWeekStart(mondayOf())}
          className="text-[11px] font-bold px-2.5 py-1 rounded-full border border-slate-300 hover:bg-slate-50">This week</button>
      </div>

      {/* Progress bar */}
      <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <div className="font-semibold inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Week summary</div>
          <div className="text-slate-500">
            <span className="font-bold tabular-nums text-slate-900">{totals.allocated.toFixed(1)}h</span> allocated /
            <span className="tabular-nums"> {totals.hours.toFixed(1)}h</span> logged
            {totals.unallocated > 0 && <span className="text-amber-600 font-semibold"> · {totals.unallocated.toFixed(1)}h pending</span>}
            <span className="text-slate-400"> · Cost {money(totals.cost)}</span>
          </div>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full transition-all ${pctAllocated >= 99 ? "bg-emerald-500" : pctAllocated > 0 ? "bg-blue-500" : "bg-slate-200"}`}
            style={{ width: `${Math.min(100, pctAllocated)}%` }} />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
      ) : data.entries.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-lg">
          No check-ins on this project for the week of {weekStart}.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-3">Technician</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right">Hours</div>
            <div className="col-span-1 text-right">Rate</div>
            <div className="col-span-1 text-right">Cost</div>
            <div className="col-span-3">Allocation</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {data.entries.map((e) => {
            const clockDate = (e.clock_in || "").slice(0, 10);
            const cost = (parseFloat(e.hours) || 0) * (parseFloat(e.hourly_rate) || 0);
            const fullyAllocated = (parseFloat(e.unallocated_hours) || 0) < 0.01 && (parseFloat(e.hours) || 0) > 0;
            const partiallyAllocated = (parseFloat(e.allocated_hours) || 0) > 0 && !fullyAllocated;
            return (
              <div key={e.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50" data-testid={`prj-alloc-row-${e.id}`}>
                <div className="col-span-3 font-semibold truncate">{e.tech_name}</div>
                <div className="col-span-2 text-slate-500 tabular-nums">{clockDate}</div>
                <div className="col-span-1 text-right tabular-nums font-semibold">{(parseFloat(e.hours) || 0).toFixed(2)}h</div>
                <div className="col-span-1 text-right tabular-nums text-slate-500">{money(parseFloat(e.hourly_rate) || 0)}/h</div>
                <div className="col-span-1 text-right tabular-nums">{money(cost)}</div>
                <div className="col-span-3 min-w-0">
                  <div className="flex items-center gap-1 flex-wrap">
                    {(e.phase_allocations || []).length === 0 ? (
                      <span className="text-[10px] text-amber-600 font-semibold uppercase inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Not allocated</span>
                    ) : (
                      (e.phase_allocations || []).map((a, i) => {
                        const ph = data.phases.find((p) => p.id === a.phase_id);
                        return (
                          <span key={i} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 tabular-nums">
                            {ph?.name || "?"}: {a.hours}h
                          </span>
                        );
                      })
                    )}
                    {partiallyAllocated && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 tabular-nums">
                        {(parseFloat(e.unallocated_hours) || 0).toFixed(2)}h left
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-span-1 text-right">
                  {data.phases.length === 0 ? (
                    <span className="text-[10px] text-slate-400 italic">No phases</span>
                  ) : (
                    <button
                      data-testid={`prj-alloc-edit-${e.id}`}
                      onClick={() => setEditing(e)}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded inline-flex items-center gap-1 ${fullyAllocated ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                    >
                      {fullyAllocated ? <><CheckCircle2 className="w-3 h-3" /> Done</> : "Allocate"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <AllocationModal
          entry={editing} phases={data.phases}
          projectId={project.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Allocation Modal — split entry hours across phases
// ---------------------------------------------------------------------------
function AllocationModal({ entry, phases, projectId, onClose, onSaved }) {
  const [rows, setRows] = useState(() => {
    const existing = entry.phase_allocations || [];
    if (existing.length > 0) return existing.map((a) => ({ phase_id: a.phase_id, hours: String(a.hours) }));
    return [{ phase_id: phases[0]?.id || "", hours: String(entry.hours) }];
  });
  const [busy, setBusy] = useState(false);

  const total = rows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0);
  const overflow = total > (parseFloat(entry.hours) || 0) + 0.01;
  const remaining = (parseFloat(entry.hours) || 0) - total;

  const updateRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows([...rows, { phase_id: phases[0]?.id || "", hours: "0" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const allocateAllTo = (phase_id) => {
    setRows([{ phase_id, hours: String(entry.hours) }]);
  };

  const save = async () => {
    if (overflow) { toast.error("Allocated hours exceed logged hours"); return; }
    setBusy(true);
    try {
      const clean = rows
        .filter((r) => r.phase_id && parseFloat(r.hours) > 0)
        .map((r) => ({ phase_id: r.phase_id, hours: parseFloat(r.hours) }));
      await api.patch(`/projects/${projectId}/time-entries/${entry.id}/allocation`, { allocations: clean });
      toast.success("Allocation saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="prj-alloc-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 className="text-sm font-semibold">Allocate hours</h3>
            <div className="text-[11px] text-slate-500">{entry.tech_name} · {(entry.clock_in || "").slice(0, 10)} · <b className="tabular-nums">{(parseFloat(entry.hours) || 0).toFixed(2)}h</b></div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          {phases.length > 1 && (
            <div className="flex flex-wrap gap-1 pb-2 border-b">
              <span className="text-[10px] font-bold uppercase text-slate-500 mr-1">Quick fill all to:</span>
              {phases.map((p) => (
                <button key={p.id}
                  data-testid={`prj-alloc-quick-${p.id}`}
                  onClick={() => allocateAllTo(p.id)}
                  className="text-[10px] font-semibold px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">
                  {p.name}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`prj-alloc-row-input-${i}`}>
                <select value={r.phase_id} onChange={(e) => updateRow(i, { phase_id: e.target.value })} className="col-span-8 rounded border border-slate-300 px-2 py-1 text-xs">
                  <option value="">— select phase —</option>
                  {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input type="number" step="0.25" min="0" value={r.hours} onChange={(e) => updateRow(i, { hours: e.target.value })}
                  className="col-span-3 rounded border border-slate-300 px-2 py-1 text-xs text-right tabular-nums" placeholder="hrs" />
                <button onClick={() => removeRow(i)} className="col-span-1 p-1 text-rose-500 rounded hover:bg-rose-50"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={addRow} className="text-[11px] font-semibold text-blue-600 hover:underline">+ Add split</button>
          </div>

          <div className={`text-xs text-right tabular-nums ${overflow ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
            Allocated: <b>{total.toFixed(2)}h</b> / {(parseFloat(entry.hours) || 0).toFixed(2)}h
            {overflow ? <span className="ml-2">⚠️ over limit</span> : remaining > 0.01 ? <span className="ml-2 text-amber-600">({remaining.toFixed(2)}h remaining)</span> : null}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300">Cancel</button>
          <button data-testid="prj-alloc-save"
            onClick={save} disabled={busy || overflow}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
          >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save</button>
        </div>
      </div>
    </div>
  );
}
